import { useMemo, useState } from 'react';
import { Button, Drawer, Grid, Layout, Menu, Spin, Tag, Avatar } from 'antd';
import { AppstoreOutlined, MenuOutlined, ReadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  IconUsers,
  IconStethoscope,
  // IconBuildingFactory2,
  IconAlertTriangle,
  IconFileText,
  IconReportAnalytics,
  IconSettings,
  IconLogout,
} from '@tabler/icons-react';
import { useAccessMatrix } from '@/entities/admin/api/hooks';
import { useAuthSession, useLogout } from '@/entities/auth/api/hooks';
import { useHomeSections } from '@/entities/home/api/hooks';
import { menuRouteFromHomeSectionKey } from '@/entities/home/lib/menuRouteFromHomeSectionKey';
import { canAccessRoute } from '@/shared/lib/permissions';
import { initialsFromDisplayName } from '@/shared/lib/displayName';
import { roleLabelFromCode } from '@/shared/constants/userRoles';
import { HeaderAlertsBell } from './HeaderAlertsBell';
import s from './RootLayout.module.scss';

const { Header, Content, Footer, Sider } = Layout;

type MenuItem = { key: string; icon: React.ReactNode; label: string };
type MenuGroup = { type: 'group'; label: string; children: MenuItem[] };

const SIDER_WIDTH = 220;

const allMenuItems: MenuGroup[] = [
  {
    type: 'group',
    label: 'Главная',
    children: [{ key: '/', icon: <AppstoreOutlined size={16} />, label: 'Дашборд' }],
  },
  {
    type: 'group',
    label: 'Модули',
    children: [
      { key: '/employees', icon: <IconUsers size={16} />, label: 'Сотрудники' },
      { key: '/medical', icon: <IconStethoscope size={16} />, label: 'Медосмотры' },
      { key: '/training', icon: <ReadOutlined size={16} />, label: 'Обучение' },
      // { key: '/sout', icon: <IconBuildingFactory2 size={16} />, label: 'СОУТ' },
      { key: '/risks', icon: <IconAlertTriangle size={16} />, label: 'Оценка рисков' },
      { key: '/siz', icon: <SafetyCertificateOutlined size={16} />, label: 'СИЗ' },
      { key: '/documents', icon: <IconFileText size={16} />, label: 'НПА и документы' },
      { key: '/reports', icon: <IconReportAnalytics size={16} />, label: 'Отчётность' },
    ],
  },
  {
    type: 'group',
    label: 'Система',
    children: [{ key: '/admin', icon: <IconSettings size={16} />, label: 'Администрирование' }],
  },
];

function menuSelectedKey(pathname: string): string {
  const nested: { prefix: string; key: string }[] = [
    { prefix: '/medical', key: '/medical' },
    { prefix: '/training', key: '/training' },
    { prefix: '/employees', key: '/employees' },
    { prefix: '/risks', key: '/risks' },
    { prefix: '/siz', key: '/siz' },
    // { prefix: '/sout', key: '/sout' },
    { prefix: '/documents', key: '/documents' },
  ];
  for (const { prefix, key } of nested) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return key;
  }
  return pathname;
}

const pageTitles: Record<string, string> = {
  '/': 'Дашборд',
  '/employees': 'Оргструктура и сотрудники',
  '/medical': 'Медосмотры и контингент',
  '/training': 'Обучение, инструктажи и стажировка',
  '/sout': 'СОУТ и рабочие места',
  '/risks': 'Оценка профессиональных рисков',
  '/siz': 'Нормы СИЗ',
  '/documents': 'НПА и нормативные документы',
  '/reports': 'Отчётность',
  '/admin': 'Администрирование',
};

const { useBreakpoint } = Grid;

export function RootLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pageTitle = pageTitles[pathname] ?? pageTitles[menuSelectedKey(pathname)] ?? 'АРМ охраны труда';
  const { data: session } = useAuthSession();
  const { data: homeSections, isSuccess: homeSectionsReady, isPending: homeSectionsPending } = useHomeSections();
  const { data: accessMatrix } = useAccessMatrix();
  const logoutMutation = useLogout();
  const user = session?.user;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sectionRoutes = useMemo(() => {
    if (!homeSectionsReady || !homeSections) return null;
    const routes = new Set<string>();
    for (const { key } of homeSections.sections) {
      const route = menuRouteFromHomeSectionKey(key);
      if (route) routes.add(route);
    }
    return routes;
  }, [homeSections, homeSectionsReady]);

  const menuItems = useMemo(() => {
    const role = user?.role;
    return allMenuItems
      .map((group) => ({
        ...group,
        children: group.children.filter((item) => {
          if (!canAccessRoute(accessMatrix, role, item.key)) return false;
          if (sectionRoutes && !sectionRoutes.has(item.key)) return false;
          return true;
        }),
      }))
      .filter((group) => group.children.length > 0);
  }, [accessMatrix, sectionRoutes, user?.role]);

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    });
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    setDrawerOpen(false);
  };

  const siderContent = (
    <div className={s['root__sider-content']}>
      <div className={s['root__sider-top']}>
        <div className={s['root__sider-header']}>
          <div className={s['root__org']}>АО «СМЗ»</div>
          <div className={s['root__app-name']}>АРМ охраны труда</div>
          <div className={s['root__tagline']}>Самарский металлургический завод</div>
        </div>
        {homeSectionsPending ? (
          <div className={s['root__sider-menu-loading']}>
            <Spin size="large" />
          </div>
        ) : (
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[menuSelectedKey(pathname)]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        )}
      </div>

      <div className={s['root__sider-bottom']}>
        <Button
          type="text"
          icon={<IconLogout size={16} />}
          className={s['root__sider-logout']}
          loading={logoutMutation.isPending}
          onClick={handleLogout}
          block
        >
          Выйти
        </Button>
      </div>
    </div>
  );

  return (
    <Layout className={s.root} style={{ ['--root-sider-width']: `${SIDER_WIDTH}px` } as React.CSSProperties}>
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          styles={{ body: { padding: 0 } }}
          className={s['root__drawer']}
        >
          {siderContent}
        </Drawer>
      ) : (
        <Sider width={SIDER_WIDTH} theme="dark" className={s['root__sider']}>
          {siderContent}
        </Sider>
      )}

      <Layout className={`${s['root__inner-layout']} ${isMobile ? s['root__inner-layout_mobile'] : ''}`}>
        <Header className={s['root__header']}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              className={s['root__burger']}
              onClick={() => setDrawerOpen(true)}
            />
          )}
          <h1 className={s['root__page-title']}>{pageTitle}</h1>
          <Tag className={s['root__role-pill']}>{user?.role ? roleLabelFromCode(user.role) : '—'}</Tag>
          <div className={s['root__header-right']}>
            <div className={s['root__user-chip']}>
              <Avatar size={28} className={s['root__avatar']}>
                {user?.display_name ? initialsFromDisplayName(user.display_name) : '?'}
              </Avatar>
              <span className={s['root__user-name']}>{user?.display_name ?? 'Пользователь'}</span>
            </div>
            <HeaderAlertsBell tone="dark" />
          </div>
        </Header>

        <Content className={s['root__content']}>
          <div className={s['root__content-inner']}>
            <Outlet />
          </div>
        </Content>

        <Footer className={`${s['root__footer']} ${isMobile ? s['root__footer_mobile'] : ''}`}>
          АРМ охраны труда · АО «Самарский металлургический завод» · Разработал{' '}
          <a href="https://kts.tech/" target="_blank" rel="noreferrer">
            KTS
          </a>
        </Footer>
      </Layout>
    </Layout>
  );
}
